"use client"
import React, {useContext, useMemo, useRef, useState} from 'react';
import {Toast} from "primereact/toast";
import {Button} from "primereact/button";
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import {Dialog} from "primereact/dialog";
import {InputText} from "primereact/inputtext";
import {LayoutContext} from "../../../../../../layout/context/LayoutContext";
import {usePagination} from "../../../../../../layout/hooks/usePagination";
import {useFetch} from "../../../../../../layout/hooks/useFetch";
import callToast, {handleChangeData, postData, validateFormFields} from "../../../../../../utils/helper";
import {webInfo} from "../../../../../../utils/webInfo";
import PageHeading from "../../../../../../Components/PageHeading";
import Label from "../../../../../../Components/Label";
import {MultiSelect, MultiSelectChangeEvent} from "primereact/multiselect";

interface InitialState {
    name: string;
    permissions: {id: number, name: string}[];
}

const RoleAndPermission = () => {
    const initialState: InitialState = {
        name: '',
        permissions: []
    };

    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const {accessToken} = useContext(LayoutContext);
    const [refresh, setRefresh] = useState<boolean>(false);
    const [formData, setFormData] = useState<InitialState>(initialState);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const toast = useRef<Toast | null>(null);

    const [modal, setModal] = useState<boolean>(false);
    const [updatedId, setUpdatedId] = useState<number | null>(null);

    // Permission filtering state
    const [filterPermissionName, setFilterPermissionName] = useState('');
    const {page, limitData, handlePageChange} = usePagination();

    const mainTitle = "Role & Permissions";
    const tableDataEndpointUrl = `/roles?page=${page}`;
    const submitFormEndpointUrl = updatedId ? `/roles/${updatedId}` : "/roles";

    // Main table data
    const { data: tableDataList, totalData, loading } = useFetch(tableDataEndpointUrl, [refresh]);

    // Raw permissions from API (filtered)
    const { data: permissionListDataRaw } = useFetch(
        `/permissions?page=1&per_page=5${filterPermissionName.length > 0 ? `&name=${filterPermissionName}` : ''}`,
        [refresh, filterPermissionName]
    );


    const selectedPermissions = useMemo(() => {
        return formData.permissions || [];
    }, [formData.permissions]);

    const permissionOptions = useMemo(() => {
        const merged = [...selectedPermissions];
        const selectionIds = new Set(merged.map(p => p.id));

        if (permissionListDataRaw) {
            permissionListDataRaw.forEach((p: any) => {
                if (!selectionIds.has(p.id)) {
                    merged.push({ id: p.id, name: p.name });
                }
            });
        }
        return merged;
    }, [permissionListDataRaw, selectedPermissions]);

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const validationRules = [{ field: 'name', label: 'Name' }];
        if (!validateFormFields(formData, validationRules, toast)) return;

        const payload = {
            name: formData.name.replace(/\s+/g, ' ').trim(),
            permissions: formData.permissions.map((p: any) => p.id)
        };

        try {
            setIsSubmitting(true);
            const response = await fetch(
                `${webInfo?.baseUrl}${submitFormEndpointUrl}`,
                postData("PUT", accessToken, payload)
            );

            if (response.ok) {
                callToast(toast, true, `${mainTitle} ${updatedId ? 'updated' : 'created'} successfully`);
                setFormData(initialState);
                setModal(false);
                setUpdatedId(null);
                setRefresh(!refresh);
            } else {
                const resData = await response.json();
                callToast(toast, false, resData.error || "Operation failed");
            }
        } catch (err) {
            console.error('Failed to update', err);
        } finally {
            setIsSubmitting(false);
        }
    };

    const actionTemplate = (rowData: any) => (
        <div className={'flex align-items-center justify-content-end'}>
            <Button
                icon={'pi pi-pencil'}
                className={'border-rounded w-2rem h-2rem'}
                onClick={() => {
                    setModal(true);
                    setUpdatedId(rowData?.id);
                    setFormData({
                        name: rowData.name,
                        permissions: rowData.permissions
                            ? rowData.permissions.map((p: any) => ({ id: p.id, name: p.name }))
                            : []
                    });
                }}
                outlined
            />
        </div>
    );

    const permissionBodyTemplate = (rowData: any) => {
        const names = rowData?.permissions?.map((p: any) => p.name).join(", ");
        return <div className={'flex flex-column gap-1'}><span className={'text-sm'}>{names}</span></div>;
    };

    return (
        <div className={"card"}>
            <Toast ref={toast}/>
            <PageHeading
                title={mainTitle}
                isCreate={true}
                createFunction={() => setModal(true)}
                back={true}
            />

            <DataTable
                value={tableDataList}
                dataKey="id"
                tableStyle={{minWidth: '50rem'}}
                paginator={tableDataList?.length > 0}
                lazy
                first={(page - 1) * limitData}
                rows={limitData}
                totalRecords={totalData}
                loading={loading}
                onPage={handlePageChange}
            >
                <Column field="name" header="Name" style={{width: '30%'}} />
                <Column field="permissions" header="Permissions" body={permissionBodyTemplate} style={{width: '60%'}} />
                <Column header={"Action"} body={actionTemplate} headerClassName={"flex justify-content-end"} />
            </DataTable>

            <Dialog
                visible={modal}
                style={{width: "50rem"}}
                header={updatedId ? `Edit ${mainTitle}` : `New ${mainTitle}`}
                onHide={() => {
                    setUpdatedId(null);
                    setModal(false);
                    setFormData(initialState);
                }}
            >
                <form onSubmit={handleSubmit}>
                    <div className="grid">
                        <div className="col-12 md:col-8">
                            <Label label={"Name"} required={!updatedId}/>
                            <InputText
                                id="name"
                                value={formData.name}
                                onChange={(e) => handleChangeData('name', e.target.value, formData, setFormData)}
                                placeholder={"Enter role name"}
                                className={`w-full`}
                            />
                        </div>
                        <div className="col-12 mt-3">
                            <Label label={"Permissions"}/>
                            <MultiSelect
                                options={permissionOptions}
                                value={selectedPermissions}
                                optionLabel="name"
                                dataKey="id"
                                display="chip"
                                placeholder="Select permissions"
                                filter
                                showClear
                                className="w-full"
                                onFilter={(e) => {
                                    if (timeoutRef.current) clearTimeout(timeoutRef.current);
                                    timeoutRef.current = setTimeout(() => {
                                        setFilterPermissionName(e.filter);
                                    }, 500);
                                }}
                                onChange={(e: MultiSelectChangeEvent) => {
                                    setFormData({ ...formData, permissions: e.value });
                                }}
                            />
                        </div>
                    </div>
                    <div className="flex gap-2 mt-4">
                        <Button
                            type="submit"
                            label={updatedId ? 'Update' : "Create"}
                            className="p-button-primary"
                            loading={isSubmitting}
                        />
                    </div>
                </form>
            </Dialog>
        </div>
    );
};

export default RoleAndPermission;